import { Dialog } from "@headlessui/react"
import React, { useEffect, useState } from "react"
import { User, TakedownBatch, UserTakedown, TakedownTrack, SongSelection } from "../../../../../types"
import { KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { users } from "../../../../../apis"
import { formatNumber, getCurrency, mapToCamelCase } from "../../../../../utility"
import Loading from "../../../../ui/Loading"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
import SongSelectionOwnershipUpdateModal from "./SongSelectionOwnershipUpdateModal"
//@ts-ignore
import CloseCircle from "../../../../../assets/admin_icons/close-circle.svg"

const columnWidths = {
  ALBUM_TITLE: "17%",
  ALBUM_UPC: "11%",
  ALBUM_DISTRIBUTOR: "17%",
  TRACK_TITLE: "17%",
  TRACK_ISRC: "11%",
  SPLIT_RATE: "9%",
  OFFER_LTM: "9%",
  ADVANCE_OFFER: "9%"
}

const AdminTakedownContractModal = ({
  selectedContractTakedown,
  handleClose,
  user
}: {
  selectedContractTakedown?: UserTakedown
  handleClose: () => void
  user: User
}) => {
  const [takedownBatch, setTakedownBatch] = useState<TakedownBatch>()
  const [loading, setLoading] = useState(false)
  const [selectedSong, setSelectedSong] = useState<SongSelection | undefined>(undefined)

  useEffect(() => {
    if (!selectedContractTakedown) {
      return
    }
    if (!selectedContractTakedown.confirmedOfferId) {
      toast.error("Confirmed Offer ID missing. Create +User Takedown with Confirmed Offer ID", errorFilledToastOptions)
      handleClose()
      return
    }
    setLoading(true)
    users(
      `/list_takedown_batch_list/?album_ids=${selectedContractTakedown.albumIds}&confirmed_offer_id=${selectedContractTakedown.confirmedOfferId}`,
      "GET"
    )
      .then((resp) => {
        const takedownBatch: TakedownBatch = mapToCamelCase(resp.data)
        setTakedownBatch(takedownBatch)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        toast.error("Error fetching takedown batch", errorFilledToastOptions)
      })
  }, [selectedContractTakedown])

  // this will check if the track has split rate related potential problems
  const checkPotentialSplitRate = (song: TakedownTrack, artistId: number | undefined) => {
    // IMPORTANT NOTE: Update confirmed offer page in Scouting too if you update here.
    const track = { ...song }
    const warnings = []
    const artistList = track?.artists?.filter((artist) => !artist.name.includes("Kahuna Artist")) || []
    const trackTitle = track?.title
    const unwantedTexts = ["feat.", ".feat", "with", "&"]
    const matchedUnwantedText = unwantedTexts.find((text: string) => trackTitle?.toLowerCase().includes(text))
    let isSingleAndMainArtist = true

    if (artistList.length > 1) {
      warnings.push(`This song has multiple artists: ${artistList.map((artist) => artist.name).join(", ")}`)
      isSingleAndMainArtist = false
    }

    if (artistList.length > 0 && artistList[0].id !== artistId) {
      warnings.push(`Offer artist is not the main artist. Main artist is: ${artistList[0]?.name}`)
      isSingleAndMainArtist = false
    }

    if (matchedUnwantedText) {
      warnings.push(`This track title includes unwanted pattern: "${matchedUnwantedText}"`)
    }

    return {warnings, isSingleAndMainArtist}
  }

  const renderBadge = (
    icon: string,
    text: string,
    fontColor: string,
    bgColor: string,
    tooltipText: string,
    index: number
  ) => {
    return (
      <KTooltip
        position={index < 4 ? "right" : "left"}
        showArrow
        padding="0px 4px"
        backgroundColor="#111"
        children={
          <div
            style={{
              backgroundColor: bgColor,
              cursor: "default"
            }}
            className={`flex flex-row items-center gap-0.5 rounded-[100px] p-1 px-2 z-10000`}
          >
            <img src={icon} />
            <KSpan text={text} color={fontColor} fontSize={12} fontWeight={500} lineHeight="14px" />
          </div>
        }
        content={
          <div className="w-[200px] h-auto whitespace-normal">
            <KSpan text={tooltipText} color="#FFF" fontSize={12} lineHeight="14px" />
          </div>
        }
      />
    )
  }

  const updateSongSelectionOwnership = async (songSelectionId: number, ownership: number) => {
    const artistId = takedownBatch?.artistId
    const userId = user?.id

    if (!artistId || !userId) return
    setLoading(true)
    users(
      `/update_takedown_song_selection_ownership/?song_selection_id=${songSelectionId}&ownership=${ownership}&artist_id=${artistId}&artist_user_id=${userId}`,
      "PUT"
    )
      .then((resp) => {
        if (resp.data.success) {
          toast.success("Split rate has been updated successfully", successFilledToastOptions)
          if (!takedownBatch) {
            setLoading(false)
            return
          }
          const trackList = { ...takedownBatch }.trackList
          const updatedTrackList = trackList?.map((track: TakedownTrack) =>
            track.songSelection.id === songSelectionId
              ? { ...track, songSelection: { ...track.songSelection, ownership } }
              : { ...track }
          )
          setTakedownBatch({ ...takedownBatch, trackList: updatedTrackList })
        } else {
          toast.error("Error on updating split rate", errorFilledToastOptions)
        }
        setSelectedSong(undefined)
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error on updating split rate", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const renderTakedownBatches = () => {
    const takedowns = { ...takedownBatch }
    const retrievedSheetValues = takedowns?.sheetValues ?? []
    const trackList = takedowns.trackList ?? []

    const artistId = takedowns.artistId // id of the artist of the confirmed offer (not spotify Id, it is primary key id)

    let splitRateExistInSheet = true
    const headerRow = retrievedSheetValues[0]
    if (!(headerRow?.length >= 14 && headerRow[13] === "split_rate")) {
      splitRateExistInSheet = false
    }

    const splitRateMissingRowNumber: { [key: string]: number } = {}
    const splitRateMap: { [key: string]: string[] } = {} // key:isrc, value: split rate

    if (splitRateExistInSheet) {
      trackList.forEach((track: TakedownTrack) => {
        // const songTitle = songSelection?.track?.title do not use this, because it takes the track title from Track model,
        // we need the one from the SongSelection model (It is more reliable when there is an encoding related problem in reports)
        const songTitle = track?.title?.trim() || ""
        const songIsrc = track?.isrc?.trim() || ""
        retrievedSheetValues.forEach((sheetRow) => {
          const rowLength = sheetRow?.length
          const rowSongTitle = sheetRow?.[3]?.trim() || ""
          const rowIsrc = sheetRow?.[4]?.trim() || ""
          if (
            ((rowIsrc && songIsrc && rowIsrc === songIsrc) ||
              (rowSongTitle && songTitle && rowSongTitle === songTitle)) &&
            rowLength < 14
          ) {
            // if length is smaller than 14 it means split_rate cell is empty.
            if (Object.hasOwn(splitRateMissingRowNumber, songIsrc)) {
              splitRateMissingRowNumber[songIsrc] += 1
            } else {
              splitRateMissingRowNumber[songIsrc] = 1
            }
          } else if (
            ((rowIsrc && songIsrc && rowIsrc === songIsrc) ||
              (rowSongTitle && songTitle && rowSongTitle === songTitle)) &&
            rowLength >= 14
          ) {
            const splitRate = sheetRow[13]
            if (!Object.hasOwn(splitRateMap, songIsrc)) {
              splitRateMap[songIsrc] = []
            }
            if (!splitRateMap[songIsrc].includes(splitRate)) {
              splitRateMap[songIsrc].push(splitRate)
            }
          }
        })
      })
    }

    return (
      <div className="mt-8">
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Platform Royalties" fontSize={16} />
          <div className="flex gap-8">
            {Object.entries(takedownBatch?.platformRoyalties || {})?.map(([platform, value]) => {
              return (
                <div key={`${platform}-${value}`}>
                  <KSpan text={`${platform}: `} />
                  <KSpan text={`${formatNumber(value, 2)} ${getCurrency(takedownBatch?.currency || "")}`} />
                </div>
              )
            })}
          </div>
        </div>
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Countries Royalties" fontSize={16} />
          <div className="flex gap-8">
            {Object.entries(takedownBatch?.countryRoyalties || {})?.map(([country, value]) => {
              return (
                <div key={`${country}-${value}`}>
                  <KSpan text={`${country.toUpperCase()}: `} />
                  <KSpan text={`${formatNumber(value, 2)} ${getCurrency(takedownBatch?.currency || "")}`} />
                </div>
              )
            })}
          </div>
        </div>
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Platforms Not Supported by Kahuna " fontSize={16} />
          <div className="flex gap-2 py-1 flex-row items-center flex-wrap">
            {!(takedownBatch?.unsupportedPlatforms && takedownBatch?.unsupportedPlatforms?.split(", ")?.length !== 0) && <KSpan text="-" />}
            {takedownBatch?.unsupportedPlatforms?.split(", ").map((unsupportedPlatform, index: number) => {
              return renderBadge(
                "/admin_icons/alert-circle.svg",
                unsupportedPlatform,
                "#EF4343",
                "#EF434333",
                "Not supported by Kahuna distribution network.",
                index
              )
            })}
          </div>
        </div>
        <div className="mb-4">
          <KSpan fontWeight={500} color="#111" text="Missing Expected Platforms in Report" fontSize={16} />
          <div className="flex gap-2 py-1 flex-row items-center flex-wrap">
            {takedownBatch?.missingPlatformsRisk?.length === 0 &&
              takedownBatch?.missingPlatformsOpportunity?.length === 0 && <KSpan text="-" />}
            {takedownBatch?.missingPlatformsRisk?.map((missingPlatform, index: number) => {
              return renderBadge(
                "/admin_icons/alert-circle.svg",
                missingPlatform,
                "#EF4343",
                "#EF434333",
                "Expected platforms missing — report may be incomplete.",
                index
              )
            })}
            {takedownBatch?.missingPlatformsOpportunity?.map((missingPlatform, index: number) => {
              return renderBadge(
                "/admin_icons/trend-up.svg",
                missingPlatform,
                "#16A249",
                "#16A24933",
                "Potential new revenue opportunity after migration.",
                index + takedownBatch?.missingPlatformsRisk?.length
              )
            })}
          </div>
        </div>
        <div>
          <div style={{ minWidth: "100%" }} className="flex mt-10">
            {Object.entries(columnWidths).map(([header, width]) => {
              return (
                <div style={{ width }} key={`header_${header}`}>
                  <KSpan color="#111" text={header} fontWeight={500} />
                </div>
              )
            })}
          </div>
        </div>
        {takedownBatch?.trackList?.map((takedownTrack: TakedownTrack) => {
          const album = takedownTrack.album
          const offer = takedownTrack.songSelection
          
          const checkSplitRateWarnings = checkPotentialSplitRate(takedownTrack, artistId)
          const trackBasedSplitRateWarnings = checkSplitRateWarnings.warnings
          const isSingleAndMainArtist = checkSplitRateWarnings.isSingleAndMainArtist
          const reportBasedSplitRateWarnings = splitRateMissingRowNumber?.[takedownTrack?.isrc?.trim() || ""] || 0
          const splitRateArray = splitRateMap?.[takedownTrack?.isrc?.trim() || ""] || []

          const showAlert =
            ((trackBasedSplitRateWarnings.length > 0) ||
              reportBasedSplitRateWarnings > 0 ||
              !splitRateExistInSheet ||
              splitRateArray.length > 1) &&
            !offer.ownership &&
            !isSingleAndMainArtist

          return (
            <div
              key={`track_${takedownTrack.id}`}
              className="mt-3 mb-3 pb-3 flex gap-2"
              style={{ borderTop: "1px dashed gray", width: "100%" }}
            >
              <div style={{ width: columnWidths.ALBUM_TITLE }}>
                <KSpan fontWeight={500} text={`${album?.title || "-"}`} />
              </div>
              <div style={{ width: columnWidths.ALBUM_UPC }}>
                <KSpan fontWeight={500} text={`${album?.upc || "-"}`} />
              </div>
              <div style={{ width: columnWidths.ALBUM_DISTRIBUTOR }}>
                <KSpan fontWeight={500} text={`${album?.distributor || "-"}`} />
              </div>
              <div style={{ width: columnWidths.TRACK_TITLE }}>
                <KSpan fontWeight={500} text={`${takedownTrack.title || "-"}`} />
              </div>
              <div style={{ width: columnWidths.TRACK_ISRC }}>
                <KSpan fontWeight={500} text={`${takedownTrack.isrc || "-"}`} />
              </div>
              <div
                style={{
                  width: columnWidths.SPLIT_RATE
                }}
              >
                <div
                  className="w-full flex flex-row items-center gap-1 pt-0.5 cursor-pointer"
                  onClick={() => {
                    setSelectedSong(offer)
                  }}
                >
                  <KSpan fontWeight={500} text={`${offer.ownership?.toString() || "-"}`} />
                  {showAlert && (
                    <KTooltip
                      width="300px"
                      content={
                        <div className="flex flex-col gap-0.5">
                          {trackBasedSplitRateWarnings.length > 0 && (
                            <div className="mb-1">
                              <KSpan text="Potential Split Required" color="#111" fontWeight={600} />
                            </div>
                          )}
                          {trackBasedSplitRateWarnings.length > 0 &&
                            trackBasedSplitRateWarnings.map((warning, i) => {
                              return <KSpan key={i} text={`• ${warning}`} color="#111" fontSize={13} />
                            })}
                          {(reportBasedSplitRateWarnings > 0 || !splitRateExistInSheet) && (
                            <React.Fragment>
                              <div className="my-1">
                                <KSpan text="Potential Split Missing in Reports" color="#111" fontWeight={600} />
                              </div>
                              {!splitRateExistInSheet && (
                                <KSpan
                                  text={`• There is no split rate column in the royalty report associated with the confirmed offer.`}
                                  color="#111"
                                  fontSize={13}
                                />
                              )}
                              {reportBasedSplitRateWarnings > 0 && (
                                <KSpan
                                  text={`• Split rate value is missing in ${reportBasedSplitRateWarnings} row(s) of the royalty report associated with the confirmed offer.`}
                                  color="#111"
                                  fontSize={13}
                                />
                              )}
                            </React.Fragment>
                          )}
                          {splitRateArray.length > 1 && (
                            <React.Fragment>
                              <div className="my-1">
                                <KSpan text="Split Rate Mismatch Detected in Reports" color="#111" fontWeight={600} />
                              </div>

                              <KSpan
                                text={`• This track has inconsistent split rates across different rows of the royalty report associated with the confirmed offer.`}
                                color="#111"
                                fontSize={13}
                              />
                              <div className="ml-2 flex flex-col gap-0.5">
                                {splitRateArray.map((splitRate, i) => {
                                  return <KSpan key={i} text={`→ ${splitRate}`} fontSize={11} color="#111" />
                                })}
                              </div>
                            </React.Fragment>
                          )}
                        </div>
                      }
                    >
                      <div className="p-1">
                        <img src="/admin_icons/alert-circle.svg" className="w-5 h-5 min-w-5 min-h-5" />
                      </div>
                    </KTooltip>
                  )}
                </div>
              </div>
              <div style={{ width: columnWidths.OFFER_LTM }}>
                <KSpan
                  fontWeight={500}
                  text={`${formatNumber(offer.ltmIncome || "0", 2)} ${getCurrency(takedownBatch.currency)}`}
                />
              </div>
              <div style={{ width: columnWidths.ADVANCE_OFFER }}>
                <KSpan
                  fontWeight={500}
                  text={`${formatNumber(offer.advanceOffer || "0", 2)} ${getCurrency(takedownBatch.currency)}`}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Dialog open={selectedContractTakedown !== undefined} onClose={() => handleClose()}>
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d] ">
        <div
          className="p-6 shadow-md rounded-[10px] bg-[#fff]"
          style={{ height: "90vh", overflowY: "scroll", minWidth: 1200, maxWidth: 1300 }}
        >
          <div>
            <KTitleSpan text="Takedown Batch Based on Distributor and Revenue" fontSize={24} />
            <KSpan
              text={`Selected User: ${selectedContractTakedown?.user?.firstName} ${selectedContractTakedown?.user?.lastName}`}
            />
            {loading && <Loading />}
            {renderTakedownBatches()}
          </div>
        </div>
      </div>
      <SongSelectionOwnershipUpdateModal
        songSelection={selectedSong}
        setSongSelection={setSelectedSong}
        onSubmit={updateSongSelectionOwnership}
        loading={loading}
      />
    </Dialog>
  )
}

export default AdminTakedownContractModal
